
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { AzavistaApiService, IEvent, IGetReportRequest, IGetReportResponse, ProductGroupEntityType } from '@azavista/servicelib';
import { IField, IObjectWithId, IValueChangedEventArgs } from '@azavista/components/shared';
import { AbstractWidgetDirective, IWidgetComponentDefinition } from '@azavista/components/widgets';
import { ISalesByProductQuantityPayload, ISalesByProductSettings, SalesByProductQuantityData, SalesByProductQuantityPresentationType } from './interfaces';
import { WidgetsUtilsService } from '../widgets-utils.service';
import { SharedService } from '../../shared.service';
import { TRANSLATION_MAP } from '../../const';
import { CommonDialogsService } from '../../common-dialogs.service';
import { IWidgetDataChanges, WidgetViewDetailsNavigationPaths } from '../interfaces';
import { SalesByProductQuantityService } from './sales-by-product-quantity.service';
import { ChartType } from '../apextypes';
import { IDataPointSelectedArguments } from '../apexcharts-wrapper/interfaces';

@Component({
    selector: 'azavista3-sales-by-product-quantity',
    templateUrl: './sales-by-product-quantity.component.html',
})
export class SalesByProductQuantityWidgetComponent extends AbstractWidgetDirective implements OnInit, OnDestroy {
    chart: ApexChart = {
        type: 'bar'
    };
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    title: ApexTitleSubtitle;
    labels: string[];
    definitionSettings: ISalesByProductSettings;
    eventId = '';
    event: IEvent;
    totalText: string;
    isEventPredefined = false;
    eventNotAvailable = false;
    fields: IFields;
    isLoaded = false;
    isPresentationTotal = false;

    declare definition: IWidgetComponentDefinition & {
        data: SalesByProductQuantityData
    };

    private destroyed$ = new Subject<void>();

    constructor(
        private readonly apiSvc: AzavistaApiService,
        private readonly widgetsUtilsSvc: WidgetsUtilsService,
        private readonly sharedSvc: SharedService,
        private readonly commonDlgsSvc: CommonDialogsService,
        private readonly salesByProductWidgetSvc: SalesByProductQuantityService,
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.definitionSettings = this.definition.settings as ISalesByProductSettings;
        this.setDefaultDefinitionSettingsIfMissing();
        this.isPresentationTotal = this.definitionSettings.local.presentationType === SalesByProductQuantityPresentationType.total;
        this.fields = {};
        this.definition.data = null;
        this.eventId = this.definition.context?.eventId;
        if (this.eventId) {
            // The widget is inside event context
            await this.setEventFromContext(this.eventId);
        } else {
            this.eventId = this.definition.settings.remote.event_id;
            if (this.eventId) {
                // Load event so we know whether it exists
                try {
                    this.event = await this.apiSvc.getEvent(this.eventId);
                    this.eventNotAvailable = false;
                } catch (err) {
                    this.eventNotAvailable = true;
                }
            }
        }

        this.setCustomWidgetParams();

        this.chart = {
            type: this.definitionSettings && this.definitionSettings.local.presentationType
                ? this.getChartTypeFromPresentationType(this.definitionSettings.local.presentationType)
                : this.chart.type,
            toolbar: { show: false },
            zoom: { enabled: false },
            height: 200,
            animations: { enabled: true },
        };

        super.subscribeToDataChanges({
            next: (newData?: IWidgetDataChanges) => {
                this.dataChanged(newData);
            }
        });

        if (this.configMode) {
            if (this.eventId) {
                // Initially add only the selected event as item in event autocomplete
                this.event ||= await this.widgetsUtilsSvc.getEvent(this.eventId);
            }
            this.salesByProductWidgetSvc.setDefaultRemoteSettingsIfMissing(this.definitionSettings, this.event?.id);
            await this.setFieldsForEvent(this.event);
            this.fields.productsValue = this.definitionSettings.remote.product_ids || [];
            this.onSettingsChanged();
        } else {
            const hasRemoteSettings = !this.salesByProductWidgetSvc.areRemoteSettingsEmpty(this.definitionSettings.remote);
            if (hasRemoteSettings) {
                // Start loading on next loop because it causes loading output to be emitted which could cause
                // Angular change detection errors in the consumer (the dashboard component) if it changes template bound variables
                // in the event handler
                window.setTimeout(() => this.loadWidgetData(this.definition));
            } else {
                // Notify parent that we need remote settings
                this.settingsNeeded.next();
            }
        }

        if (!this.definitionSettings.local.presentationType) {
            this.definitionSettings.local.presentationType = this.chart.type as any;
        }
        const { labels } = this.widgetsUtilsSvc.convertDataToSeriesAndLabels(this.definition.data);
        this.series = this.widgetsUtilsSvc.convertGroupedBarSeries(this.definition.data);
        this.labels = labels;

        this.isLoaded = true;
    }

    dataChanged(newData?: IWidgetDataChanges): void {
        let presentationTypeChanged = false;
        let shouldConvertSeries = false;

        const settings = newData.settings as ISalesByProductSettings;
        if (settings?.remote?.event_id) {
            // Settings were changed - assume event is selected - clear eventNotAvailableFlag
            // TODO: This not always reflects event existence because if no event is selected in the settings
            //       the event_id still has value - the one of possibly non-existing event
            this.eventNotAvailable = false;
        }

        // It turns out that this.definition.settings.local === settings.local and we cannot compare them
        // This probably happens because the main widget instance and the pop-up widget instance
        // are provided with the same widget definition object in azavista-dashboard component
        // TODO: Maybe we should clone this.definition.settings before assigning it to this.definitionSettings
        //       and only use this.definitionSettings later ?
        if (settings?.local) {
            if (this.isPresentationTotal && settings.local.presentationType !== SalesByProductQuantityPresentationType.total) {
                presentationTypeChanged = true;
            } else if (settings.local.presentationType !== SalesByProductQuantityPresentationType.total && this.chart.type !== settings.local.presentationType) {
                presentationTypeChanged = true;
            }
            this.isPresentationTotal = settings.local.presentationType === SalesByProductQuantityPresentationType.total;
        }
        if (newData.data) {
            shouldConvertSeries = true;
        }
        if (newData.width && this.chart.width !== newData.width) {
            this.chart.width = newData.width;
        }
        if (newData.height && this.chart.height !== newData.height) {
            this.chart.height = newData.height;
        }
        if (presentationTypeChanged || shouldConvertSeries) {
            const { labels } = this.widgetsUtilsSvc.convertDataToSeriesAndLabels(newData.data);
            this.series = this.widgetsUtilsSvc.convertGroupedBarSeries(this.definition.data);;
            this.labels = labels;
        }
        if (presentationTypeChanged) {
            this.presentationTypeChanged(settings.local.presentationType);
        }

        if (!this.configMode && settings && settings.remote) {
            // Not config mode and remote settings were changed - reload data from server
            this.loadWidgetData(this.definition);
        } else if (newData.data) {
            // this.processData(newData.data);
        }
    }

    processData(data: SalesByProductQuantityData): void {
        let sum = 0;
        // Object.keys(data).forEach(x => sum += data[x]);
        // this.totalText = sum.toFixed(2);
    }

    loadWidgetData(definition: IWidgetComponentDefinition): void {
        this.definitionSettings = definition.settings;
        if (definition.size?.height) {
            this.chart.height = definition.size.height;
            this.chart.width = definition.size.width;
        }
        if (!this.definitionSettings.remote.event_id) {
            return;
        }
        const req: IGetReportRequest<'soldproductquantities'> = {
            reportType: 'soldproductquantities',
            widgetId: definition.id,
            payload: {
                event_id: this.definitionSettings.remote.event_id,
                product_ids: this.definitionSettings.remote.product_ids,
            } as ISalesByProductQuantityPayload,
        };
        this.notifyLoading(true);
        this.apiSvc.getReport<'soldproductquantities'>(req)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: response => this.processDataLoaded(definition, response),
                error: () => this.processDataLoadedError()
            });
    }

    processDataLoaded(definition: IWidgetComponentDefinition, response: IGetReportResponse): void {
        this.notifyLoading(this.sharedSvc.getLoadingOnReportLoaded(response));
        definition.data = response.data;
        definition.dataChangedSubject.next({ data: definition.data });
    }

    processDataLoadedError(): void {
        // TODO: What if report data fails ?
        this.notifyLoading(false);
    }

    onDataPointSelected(args: IDataPointSelectedArguments): void {
        // TODO: Do we need something to happen ?
        // this.customEvent.emit({ type: WidgetCustomEventType.dataPointSelected, data: args });
    }

    async setEventFromContext(contextEventId: string): Promise<void> {
        this.isEventPredefined = true;
        this.definitionSettings.remote.event_id = contextEventId;
        try {
            this.event = await this.widgetsUtilsSvc.getEvent(contextEventId);
            this.eventNotAvailable = !this.event;
        } catch (err) {
            this.eventNotAvailable = true;
        }
    }

    async onSelectEvent(): Promise<void> {
        const title = this.sharedSvc.translate(TRANSLATION_MAP.EVENTS);
        const dlgResult = await this.commonDlgsSvc.showSelectEventsDialog(title, true);
        if (!dlgResult || dlgResult.length === 0) {
            return;
        }
        this.event = dlgResult[0] as IEvent;
        const eventId = this.event ? this.event.id : null;
        this.definitionSettings.remote.event_id = eventId;
        this.eventId = eventId;
        this.setCustomWidgetParams();
        await this.setFieldsForEvent(this.event);
        this.definitionSettings.remote.product_ids = [];
        this.onSettingsChanged();
    }

    setCustomWidgetParams(): void {
        this.definition.viewButtonText = this.sharedSvc.translate(TRANSLATION_MAP.GO_TO_EVENT);
        this.definition.viewDetailsNavPath = WidgetViewDetailsNavigationPaths.event;
        this.definition.viewDetailsNavQueryParams = this.widgetsUtilsSvc.getViewDetailsNavQueryParamsForEvent(this.eventId);
    }

    async setFieldsForEvent(event: IEvent): Promise<void> {
        if (!event) {
            this.fields.products = null;
            return;
        }
        const eventProducts = await this.apiSvc.getAllProductsForEntity(ProductGroupEntityType.event, this.eventId);
        this.fields.products = this.sharedSvc.createMultiSelectFieldValueWithAttributeTranslations(eventProducts as IObjectWithId[], 'product_id');
        this.fields.productsValue = [];
    }

    getChartTypeFromPresentationType(presentationType: SalesByProductQuantityPresentationType): ChartType {
        switch (presentationType) {
            case SalesByProductQuantityPresentationType.barChart:
                return 'bar';
            default:
                return 'bar';
        }
    }

    presentationTypeChanged(newType: SalesByProductQuantityPresentationType): void {
        this.isPresentationTotal = newType === SalesByProductQuantityPresentationType.total;
        if (newType !== SalesByProductQuantityPresentationType.total) {
            this.chart.type = newType;
            this.series = this.widgetsUtilsSvc.convertGroupedBarSeries(this.definition.data);;
        }
    }

    onProductsChanged(args: IValueChangedEventArgs): void {
        this.definitionSettings.remote.product_ids = args.value;
        this.onSettingsChanged();
    }

    onSettingsChanged(): void {
        const settingsAreValid = this.salesByProductWidgetSvc.areSettingsValid(this.definitionSettings);
        this.settingsChanged.next({
            definition: this.definition,
            settings: this.definitionSettings,
            settingsAreValid: settingsAreValid
        });
    }

    setDefaultDefinitionSettingsIfMissing(): void {
        this.definitionSettings ||= { local: {}, remote: {} } as ISalesByProductSettings;
        this.definitionSettings.local ||= { presentationType: SalesByProductQuantityPresentationType.barChart };
        this.definitionSettings.remote ||= { event_id: '', product_ids: [] };
        if (!this.definitionSettings.local.presentationType) {
            this.definitionSettings.local.presentationType = SalesByProductQuantityPresentationType.barChart;
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        super.destroy();
    }
}

interface IFields {
    products?: IField;
    productsValue?: string[];
}
